/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable, NgZone, inject, OnDestroy } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Subject } from 'rxjs';
import {
  clearVaultNavigationCache,
  clearVaultSessionCookies,
  clearVaultSessionStorage,
} from './vault-session-storage.util';

@Injectable({ providedIn: 'root' })
export class VaultStorageService implements OnDestroy {
  public storage$ = new Subject<any>();
  readonly ngZone = inject(NgZone);
  readonly cookieService = inject(CookieService);
  private intervalId: any;
  private cookieSyncPaused = false;

  constructor() {
    this.loadFromCookies();
    this.listenForCookieChanges();
  }

  private loadFromCookies(): void {
    if (this.cookieSyncPaused) {
      return;
    }

    const cookieData = this.getDataFromCookie();

    if (cookieData) {
      this.ngZone.run(() => {
        if (cookieData.accessToken) {
          localStorage.setItem('access_token', cookieData.accessToken);
        }

        if (cookieData.refreshToken) {
          localStorage.setItem('refresh_token', cookieData.refreshToken);
        }

        if (cookieData.userData) {
          localStorage.setItem('user_data', cookieData.userData);
        }

        if (cookieData.tokenExpiry) {
          localStorage.setItem('token_expiry', cookieData.tokenExpiry);
        }

        if (cookieData.hasMultipleModules !== undefined) {
          localStorage.setItem('hasMultipleModules', cookieData.hasMultipleModules);
          sessionStorage.setItem('hasMultipleModules', cookieData.hasMultipleModules);
        }

        if (cookieData.selectedProductId !== undefined) {
          localStorage.setItem('selectedProductId', cookieData.selectedProductId);
          sessionStorage.setItem('selectedProductId', cookieData.selectedProductId);
        }

        if (cookieData.selectedRoute !== undefined) {
          localStorage.setItem('selectedRoute', cookieData.selectedRoute);
          sessionStorage.setItem('selectedRoute', cookieData.selectedRoute);
        }

        if (cookieData.selectedModule !== undefined) {
          localStorage.setItem('selectedModule', cookieData.selectedModule);
          sessionStorage.setItem('selectedModule', cookieData.selectedModule);
        }

        this.storage$.next({
          accessToken: localStorage.getItem('access_token'),
          refreshToken: localStorage.getItem('refresh_token'),
          userData: localStorage.getItem('user_data'),
          tokenExpiry: localStorage.getItem('token_expiry'),
          hasMultipleModules: localStorage.getItem('hasMultipleModules'),
          selectedProductId: localStorage.getItem('selectedProductId'),
          selectedRoute: localStorage.getItem('selectedRoute'),
          selectedModule: localStorage.getItem('selectedModule'),
        });
      });
    }
  }

  private listenForCookieChanges(): void {
    let lastCookieValue = this.cookieService.get('app_data');

    this.intervalId = setInterval(() => {
      const currentCookieValue = this.cookieService.get('app_data');
      if (currentCookieValue !== lastCookieValue) {
        lastCookieValue = currentCookieValue;
        this.loadFromCookies();
      }
    }, 1000);
  }

  public getDataFromCookie(): any {
    const cookie = this.cookieService.get('app_data');
    if (cookie) {
      try {
        return JSON.parse(cookie);
      } catch (_e) {
        return null;
      }
    }
    return null;
  }

  public getAccessToken(): string | null {
    return localStorage.getItem('access_token');
  }

  public getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  public getUserData(): any {
    const userData = localStorage.getItem('user_data');
    if (userData) {
      try {
        return JSON.parse(userData);
      } catch (_e) {
        return null;
      }
    }
    return null;
  }

  public getTokenExpiry(): number | null {
    const expiry = localStorage.getItem('token_expiry');
    return expiry ? parseInt(expiry, 10) : null;
  }

  public isTokenValid(): boolean {
    const expiry = this.getTokenExpiry();
    if (!expiry) return false;
    return Date.now() < expiry - 30000;
  }

  public getTokenRemainingTime(): number | null {
    const expiry = this.getTokenExpiry();
    if (!expiry) return null;
    const remaining = expiry - Date.now();
    return remaining > 0 ? remaining : 0;
  }

  public getHasMultipleModules(): boolean {
    const localValue = localStorage.getItem('hasMultipleModules');
    if (localValue !== null) {
      return localValue === 'true';
    }

    const cookieData = this.getDataFromCookie();
    if (cookieData && cookieData.hasMultipleModules !== undefined) {
      return cookieData.hasMultipleModules === 'true';
    }

    return false;
  }

  public getSelectedProductId(): string | null {
    return localStorage.getItem('selectedProductId');
  }

  public getSelectedRoute(): string | null {
    return localStorage.getItem('selectedRoute');
  }

  public getSelectedModule(): any {
    const moduleData = localStorage.getItem('selectedModule');
    if (moduleData) {
      try {
        return JSON.parse(moduleData);
      } catch (_e) {
        return null;
      }
    }
    return null;
  }

  public hasValidData(): boolean {
    return !!this.getDataFromCookie();
  }

  public clearAll(): void {
    this.cookieSyncPaused = true;
    clearVaultSessionStorage();
    clearVaultNavigationCache();
    clearVaultSessionCookies(this.cookieService);

    this.ngZone.run(() => {
      this.storage$.next({
        accessToken: null,
        refreshToken: null,
        userData: null,
        tokenExpiry: null,
        hasMultipleModules: null,
        selectedProductId: null,
        selectedRoute: null,
        selectedModule: null,
      });
    });
  }

  public resumeCookieSync(): void {
    this.cookieSyncPaused = false;
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }
}
